import { useId } from 'react';

type Option = string | { value: string; label: string };

const valueOf = (o: Option) => (typeof o === 'string' ? o : o.value);
const labelOf = (o: Option) => (typeof o === 'string' ? o : o.label);

/**
 * Escolha de várias opções entre poucas, todas à vista — a lista de verificação
 * vertical do desenho de origem (ex.: "Documentação Complementar").
 *
 * Não é o `MultiSelect` da marca: aquele é para lista LONGA, com busca dentro de
 * um popover — a régua dele mesmo diz que nasceu para filtro de muitas opções.
 * Aqui a lista é curta (três, quatro itens) e o desenho de origem já a mostra
 * inteira, sem busca. Local ao módulo pela mesma razão do `RadioGroup`.
 *
 * Controlado quando recebe `selected` e `onChange` (a mesa da Análise, desde
 * 06/10/2026); sem eles, é o desenho dos pipes, que não grava nada.
 */
export function CheckboxGroup({
  label,
  required,
  help,
  options,
  selected,
  onChange,
  disabled,
  error,
}: {
  label: string;
  required?: boolean;
  help?: string;
  options: readonly Option[];
  selected?: readonly string[];
  onChange?: (selected: string[]) => void;
  disabled?: boolean;
  error?: string | null;
}) {
  const idLegend = useId();
  const controlled = selected !== undefined && onChange !== undefined;
  const describedBy =
    [help ? `${idLegend}-ajuda` : null, error ? `${idLegend}-erro` : null]
      .filter(Boolean)
      .join(' ') || undefined;

  const toggle = (value: string) => {
    if (!controlled) return;
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    /* Na ordem das opções, e não na ordem do clique. */
    onChange(options.map(valueOf).filter((v) => next.includes(v)));
  };

  return (
    <div className="flex flex-col gap-1.5">
      <fieldset aria-describedby={describedBy} disabled={disabled}>
        <legend id={idLegend} className="mb-1.5 text-xs font-medium text-texto">
          {label}
          {required && (
            <>
              <span aria-hidden="true" className="ml-0.5 text-erro">
                *
              </span>
              <span className="sr-only"> (obrigatório)</span>
            </>
          )}
        </legend>

        <div className="flex flex-col gap-2">
          {options.map((option) => (
            <label
              key={valueOf(option)}
              className={`flex min-h-9 cursor-pointer items-center gap-2 rounded-borda border px-3 text-sm text-texto transition-[border-color,background-color] hover:border-texto-suave ${error ? 'border-erro' : 'border-borda-forte'}`}
            >
              <input
                type="checkbox"
                value={valueOf(option)}
                {...(controlled
                  ? {
                      checked: selected.includes(valueOf(option)),
                      onChange: () => toggle(valueOf(option)),
                    }
                  : {})}
                className="size-4 shrink-0 rounded border-borda-forte accent-primaria"
              />
              <span className="min-w-0 truncate">{labelOf(option)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {help && (
        <p id={`${idLegend}-ajuda`} className="text-rotulo text-texto-suave">
          {help}
        </p>
      )}
      {error && (
        <p id={`${idLegend}-erro`} className="text-rotulo text-erro">
          {error}
        </p>
      )}
    </div>
  );
}
